import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { AppProvider } from "./context/AppContext";
import ScrollToTop from "./components/ScrollToTop";
import MenuPage from "./pages/MenuPage";
import CheckoutPage from "./pages/CheckoutPage";
import PaymentPage from "./pages/PaymentPage";
import OrderStatusPage from "./pages/OrderStatusPage";
import AuthPage from "./pages/AuthPage";
import AccountPage from "./pages/AccountPage";
import AdminPage from "./pages/AdminPage";
import ReviewsPage from "./pages/ReviewsPage";
import ReviewSubmitPage from "./pages/ReviewSubmitPage";
import StaticPage from "./pages/StaticPage";
import SuperAdminPage from "./pages/SuperAdminPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import RewardsPage from "./pages/RewardsPage";

const SUPER_ADMIN_HOST = (process.env.REACT_APP_SUPER_ADMIN_HOST || "console.orderkit.co.uk").toLowerCase();
const SUPER_ADMIN_URL = `https://${SUPER_ADMIN_HOST}`;
const STAGING_BASE_DOMAIN = (process.env.REACT_APP_STAGING_BASE_DOMAIN || "staging.orderkit.co.uk").toLowerCase();

function TenantSuperAdminRedirect() {
  useEffect(() => {
    window.location.replace(SUPER_ADMIN_URL);
  }, []);

  return <div className="min-h-screen bg-white" aria-label="Redirecting to Orderkit Console" />;
}

function App() {
  const hostname = window.location.hostname.toLowerCase();
  const isLocalDevelopment = process.env.NODE_ENV !== "production"
    && (hostname === "localhost" || hostname === "127.0.0.1");
  const isSuperAdminHost = hostname === SUPER_ADMIN_HOST || isLocalDevelopment;
  const isStagingHost = hostname.endsWith(`.${STAGING_BASE_DOMAIN}`);

  useEffect(() => {
    if (!isStagingHost) return undefined;
    let tag = document.querySelector('meta[name="robots"]');
    const createdTag = !tag;
    const previousValue = tag?.getAttribute('content');
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('name', 'robots');
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', 'noindex, nofollow, noarchive, nosnippet, noimageindex');
    return () => {
      if (createdTag) tag.remove();
      else if (previousValue == null) tag.removeAttribute('content');
      else tag.setAttribute('content', previousValue);
    };
  }, [isStagingHost]);

  useEffect(() => {
    // The console is deliberately separate from restaurant storefront data.
    if (isSuperAdminHost) return undefined;

    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/restaurant`)
      .then(res => res.json())
      .then(rest => {
        const color = rest.primaryColor || "#f59e0b";

        const hexToRgb = (hex) => {
          const clean = hex.replace("#", "");
          const num = parseInt(clean, 16);
          return {
            r: (num >> 16) & 255,
            g: (num >> 8) & 255,
            b: num & 255,
          };
        };

        const mix = (rgb, amount) => {
          const target = amount > 0 ? 255 : 0;
          const p = Math.abs(amount);
          return `${Math.round(rgb.r + (target - rgb.r) * p)} ${Math.round(
            rgb.g + (target - rgb.g) * p
          )} ${Math.round(rgb.b + (target - rgb.b) * p)}`;
        };

        const rgb = hexToRgb(color);
        const root = document.documentElement;

        root.style.setProperty("--brand-50", mix(rgb, 0.92));
        root.style.setProperty("--brand-100", mix(rgb, 0.82));
        root.style.setProperty("--brand-200", mix(rgb, 0.68));
        root.style.setProperty("--brand-300", mix(rgb, 0.48));
        root.style.setProperty("--brand-400", mix(rgb, 0.25));
        root.style.setProperty("--brand-500", `${rgb.r} ${rgb.g} ${rgb.b}`);
        root.style.setProperty("--brand-600", mix(rgb, -0.12));
        root.style.setProperty("--brand-700", mix(rgb, -0.25));
        root.style.setProperty("--brand-800", mix(rgb, -0.38));
        root.style.setProperty("--brand-900", mix(rgb, -0.5));
      });
  }, [isSuperAdminHost]);

  if (isSuperAdminHost) {
    return (
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<SuperAdminPage />} />
            <Route path="/tenants/:domain" element={<SuperAdminPage />} />
            <Route path="/super-admin" element={<Navigate replace to="/" />} />
            <Route path="*" element={<Navigate replace to="/" />} />
          </Routes>
        </BrowserRouter>
      </div>
    );
  }

  return (
    <AppProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<MenuPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/payment" element={<PaymentPage />} />
            <Route path="/order-status/:orderId" element={<OrderStatusPage />} />
            <Route path="/login" element={<AuthPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/super-admin" element={<TenantSuperAdminRedirect />} />
            <Route path="/reviews" element={<ReviewsPage />} />
            <Route path="/reviews/submit/:token" element={<ReviewSubmitPage />} />
            <Route path="/rewards" element={<RewardsPage />} />
            {/* Clean SEO URLs */}
            <Route path="/about-us" element={<StaticPage forcedSlug="about-us" />} />
            <Route path="/privacy-policy" element={<StaticPage forcedSlug="privacy-policy" />} />
            <Route path="/terms-and-conditions" element={<StaticPage forcedSlug="terms-and-conditions" />} />
            {/* Legacy route (keep this) */}
            <Route path="/page/:slug" element={<StaticPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* Category URLs use the same ordering page, so availability,
                closures and pause-order safeguards cannot drift. */}
            <Route path="/:categorySlug" element={<MenuPage />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AppProvider>
  );
}

export default App;
